import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { auditLogApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, ShieldAlert, Download, Calendar, Info, CheckCircle2 } from 'lucide-react';
import styles from './_auth.settings.audit-log.module.css';

export const Route = createFileRoute('/_auth/settings/audit-log')({
  component: AuditLogSettingsPage,
});

export default function AuditLogSettingsPage() {
  const { platformUser } = useAuth();
  const isSuperAdmin = platformUser?.isSuperAdminTenant || false;

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 16); // format: YYYY-MM-DDTHH:MM
  });

  const [to, setTo] = useState(() => {
    return new Date().toISOString().slice(0, 16);
  });

  const [scope, setScope] = useState<'tenant' | 'platform'>('tenant');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleExport(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const fromIso = from ? new Date(from).toISOString() : undefined;
      const toIso = to ? new Date(to).toISOString() : undefined;

      let data: string;
      let filename = '';

      if (scope === 'platform' && isSuperAdmin) {
        data = await auditLogApi.adminExport(fromIso, toIso);
        filename = `enska-platform-audit-logs_${new Date().toISOString().slice(0, 10)}.jsonl`;
      } else {
        data = await auditLogApi.export(fromIso, toIso);
        filename = `enska-tenant-${platformUser?.tenant.slug}-audit-logs_${new Date().toISOString().slice(0, 10)}.jsonl`;
      }

      if (!data || data.trim() === '') {
        throw new Error('No audit log entries found for the selected time range.');
      }

      // Download file via Blob
      const blob = new Blob([data], { type: 'application/x-jsonlines' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      setSuccess(`Export completed successfully. Downloaded: ${filename}`);
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to export audit logs');
    } finally {
      setLoading(false);
    }
  }

  // Pre-formatted JSONL preview row
  const jsonlPreview = JSON.stringify({
    id: "5a8b7c9d-1234-5678-abcd-ef0123456789",
    timestamp: new Date().toISOString(),
    actorEmail: platformUser?.email || "operator@enska.ai",
    action: "SSO_CONFIG_UPDATED",
    resourceType: "Tenant",
    resourceId: platformUser?.tenant.id || "ten_019238",
    changes: {
      ssoEnabled: { from: false, to: true },
      providerType: { from: null, to: "saml" }
    },
    ipAddress: "12.34.56.78",
    userAgent: "Mozilla/5.0...",
    impersonatedBy: null
  }, null, 2);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}><FileText size={24} /> Audit Logs</h1>
        <p className={styles.subtitle}>Export SOC2-grade immutable event and operation logs for compliance auditing</p>
      </div>

      <div className={styles.alert + ' ' + styles.alertInfo}>
        <Info size={20} style={{ flexShrink: 0 }} />
        <div>
          <strong>Immutable Ledger</strong>
          <p style={{ margin: '0.25rem 0 0' }}>
            To comply with SOC2/ISO27001 requirements, audit logs are immutable. Any database modifications, updates, or deletions are permanently blocked at the engine layer.
          </p>
        </div>
      </div>

      {success && (
        <div className={styles.alert + ' ' + styles.alertSuccess}>
          <CheckCircle2 size={20} style={{ flexShrink: 0 }} />
          <div>
            <strong>Success</strong>
            <p style={{ margin: '0.25rem 0 0' }}>{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.alert + ' ' + styles.alertError}>
          <ShieldAlert size={20} style={{ flexShrink: 0 }} />
          <div>
            <strong>Export Failed</strong>
            <p style={{ margin: '0.25rem 0 0' }}>{error}</p>
          </div>
        </div>
      )}

      <div className={styles.grid}>
        {/* Left Side: Parameters Form */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}><Calendar size={18} /> Export Log Data</h2>
          <form onSubmit={handleExport} className={styles.form}>
            {isSuperAdmin && (
              <div className={styles.field}>
                <label className={styles.label}>Export Scope</label>
                <select
                  className={styles.select}
                  value={scope}
                  onChange={(e) => setScope(e.target.value as 'tenant' | 'platform')}
                  disabled={loading}
                >
                  <option value="tenant">Current Tenant ({platformUser?.tenant.name})</option>
                  <option value="platform">Platform-wide (All Tenants / Global)</option>
                </select>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label}>From Date/Time</label>
              <input
                type="datetime-local"
                className={styles.input}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>To Date/Time</label>
              <input
                type="datetime-local"
                className={styles.input}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Export Format</label>
              <select className={styles.select} disabled>
                <option value="jsonl">JSON Lines (.jsonl) — Recommended for SIEM ingestion</option>
              </select>
            </div>

            <button
              id="export-audit-logs-btn"
              type="submit"
              className={styles.btn}
              disabled={loading}
            >
              <Download size={16} />
              {loading ? 'Generating Export...' : 'Download Export'}
            </button>
          </form>
        </div>

        {/* Right Side: Log Schema Info & JSONL Row Preview */}
        <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <h2 className={styles.cardTitle}>JSONL Output Format</h2>
            <p style={{ color: 'var(--color-text-3)', fontSize: '0.8125rem', marginBottom: '0.75rem', lineHeight: '1.4' }}>
              Logs are exported in line-delimited JSON format (JSON Lines), ready for indexing in log management platforms (Splunk, Datadog, BigQuery, etc.).
            </p>
            <div className={styles.previewBox}>
              {jsonlPreview}
            </div>
          </div>

          <div className={styles.infoList}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Impersonation Flagging</span>
              <span className={styles.infoValue}>
                Operations performed by support staff via active impersonation sessions are recorded with the `impersonatedBy` email field populated.
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Tracked Events</span>
              <span className={styles.infoValue}>
                Logs track agent prompts changes, user role upgrades, SSO adjustments, model provider API key overrides, custom sandboxed tool execution, and manual handoffs.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
